import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  IEntityUser,
  IUser,
  IUserAccount,
  IUserEntity,
} from '@boma/client-shared';
import { Observable, Subscription } from 'rxjs';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import {
  loadArchivedUsers,
  loadUsers,
  restoreArchivedUser,
  UsersFacade,
} from '@boma/supplier-portal/users';
import { ConfirmationService, MessageService } from 'primeng/api';
import { BreadcrumbService } from '@boma/ui';
import { getUser } from '@boma/supplier-portal/shared/state';

@Component({
  selector: 'boma-archived-users',
  templateUrl: './archived-users.component.html',
  styleUrls: ['./archived-users.component.scss'],
})
export class ArchivedUsersComponent implements OnInit, OnDestroy {
  columns = [
    {
      field: 'user',
      header: 'Name',
      converter: (val: IUserAccount) => val.firstName,
    },
    {
      field: 'user',
      header: 'Surname',
      converter: (val: IUserAccount) => val.surname,
    },
    {
      field: 'identificationNumber',
      header: 'ID Number',
    },
    {
      field: 'employeeNumber',
      header: 'Employee Number',
    },
    {
      field: 'position',
      header: 'Position',
    },
    {
      field: 'companyContactNumber',
      header: 'Company Contact Number',
    },
    {
      field: 'personalContactNumber',
      header: 'Personal Contact Number',
    },
    // TODO: Uncomment after number is exposed on backend endpoint.
    // Verify field name 'bomaContactNumber'
    {
      field: 'user',
      header: 'Boma Number',
      converter: (val: IUserAccount) => val.bomaContactNumber,
    },
    {
      field: 'department',
      header: 'Department',
    },
  ];

  loading$ = this.usersFacade.loaded$;
  user$: Observable<IUserEntity>; // The currently logged in user - we need to extract the supplier entity from this user
  users$: Observable<IEntityUser[]>; // the result list of users
  paginationData$: Observable<any>;

  currentPage: number = null;
  perPage = 25;

  userSubscription: Subscription;

  createFormGroup: FormGroup = new FormGroup({
    identificationNumber: new FormControl('', Validators.required),
    employeeNumber: new FormControl('', Validators.required),
    position: new FormControl('', Validators.required),
    companyContactNumber: new FormControl('', Validators.required),
    personalContactNumber: new FormControl('', Validators.required),
    department: new FormControl('', Validators.required),
    notes: new FormControl('', Validators.required),
  });

  customActions = [
    {
      icon: 'material-icons-round mi-person-add',
      toolTip: 'Restore User',
    },
  ];

  query = '';

  currentUser: IUser;

  constructor(
    private store: Store,
    private router: Router,
    private usersFacade: UsersFacade,
    private breadcrumbService: BreadcrumbService,
    private confirmationService: ConfirmationService
  ) {
    this.breadcrumbService.setItems([{ label: 'Users' }]);
  }

  ngOnInit(): void {
    // Get the currently logged in user
    this.user$ = this.store.select(getUser); // get the current user
    this.userSubscription = this.user$.subscribe((x) => (this.currentUser = x));
    this.usersFacade.dispatch(
      loadArchivedUsers({ query: this.query, page: 1, perPage: this.perPage })
    ); // get a list of user entities

    // Get a list of all the users
    this.users$ = this.usersFacade.archivedUsers$;
    this.paginationData$ = this.usersFacade.paginatedData$;
  }

  handleChangePageClicked(ev = null) {
    if (ev === this.currentPage) return;
    this.currentPage = ev;
    this.usersFacade.dispatch(
      loadArchivedUsers({
        query: this.query,
        page: ev,
        perPage: this.perPage,
      })
    );
  }

  search(query) {
    console.log('SEARCHING', query);
    this.query = query;
    this.usersFacade.dispatch(
      loadArchivedUsers({ query: this.query, page: 1, perPage: this.perPage })
    );
  }

  // View a user
  onView(data) {
    let entityId = '';
    this.user$.subscribe((x) => (entityId = x.selectedOrganizationId));
    this.router.navigate(['/users/details', entityId, data.user.id]);
  }

  restoreUser(event) {
    this.confirmationService.confirm({
      header: 'Restore User?',
      accept: () => {
        this.store.dispatch(restoreArchivedUser({ userId: event.data.id }));
      },
      key: 'archivedUsers',
    });
  }

  ngOnDestroy() {
    this.userSubscription?.unsubscribe();
  }
}
